/**
 * Sprint P21 · Feb 2026 · Cockpit Assay-Disc Layer.
 *
 * P0 upgrade (Feb 2026): disc positions come from the backend in world-UTM
 * space; they are converted to local scene space via `worldToScene` before
 * being written to the InstancedMesh matrix. This keeps discs registered
 * with the block model and drillhole traces.
 */
import { useMemo } from "react";
import * as THREE from "three";
import { worldToScene, isValidCoordinate } from "@/lib/sceneCoords";


export default function AssayDiscsLayer({
  discs,
  visibility = true,
  origin,
  verticalExaggeration = 1,
}) {
  const grouped = useMemo(() => {
    if (!visibility || !discs?.length || !origin) return [];
    const byColour = new Map();
    for (const d of discs) {
      if (!isValidCoordinate(d.x, d.y, d.z)) continue;
      if (!byColour.has(d.colour)) byColour.set(d.colour, []);
      byColour.get(d.colour).push(d);
    }
    return Array.from(byColour.entries()).map(([colour, arr]) => ({ colour, arr }));
  }, [discs, visibility, origin]);

  if (!visibility) return null;
  return (
    <group name="assay-discs-overlay">
      {grouped.map(({ colour, arr }) => (
        <InstancedDiscs key={colour} colour={colour} discs={arr}
                        origin={origin} verticalExaggeration={verticalExaggeration} />
      ))}
    </group>
  );
}


function InstancedDiscs({ colour, discs, origin, verticalExaggeration }) {
  const ref = useMemo(() => new THREE.Object3D(), []);
  const geometry = useMemo(() => new THREE.CylinderGeometry(1, 1, 0.35, 20), []);
  const material = useMemo(
    () => new THREE.MeshStandardMaterial({ color: colour, metalness: 0.2, roughness: 0.6 }),
    [colour],
  );

  const setRef = (mesh) => {
    if (!mesh) return;
    for (let i = 0; i < discs.length; i++) {
      const d = discs[i];
      const p = worldToScene(d.x, d.y, d.z, origin, verticalExaggeration);
      ref.position.copy(p);
      ref.scale.set(d.radius, 1, d.radius);
      ref.rotation.set(Math.PI / 2, 0, 0);
      ref.updateMatrix();
      mesh.setMatrixAt(i, ref.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingBox?.();
    mesh.computeBoundingSphere?.();
  };

  return (
    <instancedMesh ref={setRef}
                   args={[geometry, material, discs.length]}
                   castShadow receiveShadow />
  );
}
